import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw } from 'lucide-react';
import type {
  IntegrationCodeGapPageDto,
  IntegrationCodeMapDto,
  IntegrationConnectionDto,
  IntegrationEnvironment,
  IntegrationLocalType,
  IntegrationMessageDetailDto,
  IntegrationMessageDto,
  IntegrationMessagePageDto,
  IntegrationMessageStatus,
  IntegrationProviderCode,
} from '@his/shared';
import { INTEGRATION_MESSAGE_STATUSES, nphiesOperationPermission, providerSettingsPermission } from '@his/shared';
import { del, get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { visibleInterval } from '@/lib/polling';
import { dateTime, humanize, relative } from '@/lib/utils';
import { DataTable, Dialog, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, Select, StatusBadge, Textarea, statusLabel } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { NphiesArchivePanel, NphiesReplyTemplatesPanel, NphiesSetupPanels } from './NphiesSetupPanels';

const REFRESH_MS = 60_000;

const STATUS_BADGE: Record<IntegrationMessageStatus, 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  queued: 'warning',
  sending: 'info',
  succeeded: 'success',
  rejected: 'destructive',
  failed: 'destructive',
  offline: 'warning',
  cancelled: 'secondary',
};

const CONNECTIVITY_BADGE = {
  online: { text: 'Internet reachable', variant: 'success' as const },
  offline: { text: 'No internet route', variant: 'warning' as const },
  unknown: { text: 'Not tried yet', variant: 'secondary' as const },
};

function ConnectionCard({ connection, readOnly }: { connection: IntegrationConnectionDto; readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(connection.enabled);
  const [environment, setEnvironment] = useState<IntegrationEnvironment>(connection.environment);
  const [baseUrl, setBaseUrl] = useState(connection.baseUrl ?? '');
  const [settings, setSettings] = useState<Record<string, string>>(connection.settings);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);

  const info = connection.info;
  const locked = readOnly || !connection.licensed || !connection.runsHere;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['integration-connections'] });

  const save = useMutation({
    mutationFn: () =>
      put<IntegrationConnectionDto>(`/integrations/connections/${connection.provider}`, {
        enabled,
        environment,
        baseUrl: baseUrl.trim() || null,
        settings: Object.fromEntries(Object.entries(settings).map(([key, value]) => [key, value.trim()])),
        ...(Object.values(credentials).some((value) => value.length > 0)
          ? { credentials: Object.fromEntries(Object.entries(credentials).filter(([, value]) => value.length > 0)) }
          : {}),
      }),
    onSuccess: () => {
      setCredentials({});
      refresh();
      toast.success(tr('admin', 'x_saved', { label: info.label }));
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save'), describeError(error)),
  });

  const test = useMutation({
    mutationFn: () => post<{ ok: boolean; detail: string }>(`/integrations/connections/${connection.provider}/test`, {}),
    onSuccess: (result) => {
      setTestResult(result);
      refresh();
    },
    onError: (error) => toast.error(tr('admin', 'could_not_run_the_test'), describeError(error)),
  });

  const connectivity = CONNECTIVITY_BADGE[connection.connectivity];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Plug className="h-4 w-4" /> {info.label}
        </CardTitle>
        <Badge variant={connection.licensed ? 'success' : 'secondary'}>{connection.licensed ? tr('admin', 'in_your_plan') : tr('admin', 'not_in_your_plan')}</Badge>
        {connection.licensed && <Badge variant={connectivity.variant}>{connectivity.text}</Badge>}
        {connection.queued > 0 && <Badge variant="warning">{connection.queued} {tr('admin', 'waiting_to_send')}</Badge>}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{info.description}</p>
        {!connection.licensed && <Alert>{tr('admin', 'this_service_is_not_part_of_your_plan_ask_the')}</Alert>}
        {connection.licensed && !connection.runsHere && (
          <Alert variant="warning">
            {tr('admin', 'this_facility_runs_on_its_own_server_requests')}
          </Alert>
        )}
        {connection.licensed && connection.enabled && connection.missing.length > 0 && (
          <Alert variant="warning">{tr('admin', 'not_ready_yet')} {connection.missing.join(', ')}.</Alert>
        )}

        <Checkbox checked={enabled} disabled={locked} onCheckedChange={setEnabled} label={tr('admin', 'use_this_service_from_this_computer')} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={tr('admin', 'environment')} hint={tr('admin', 'test_in_the_sandbox_first_switch_to_production')}>
            <ChoiceButtons
              value={environment}
              disabled={locked}
              onChange={setEnvironment}
              options={[
                { value: 'sandbox', label: tr('admin', 'sandbox_testing') },
                { value: 'production', label: tr('admin', 'production_live') },
              ]}
            />
          </Field>
          <Field label={tr('admin', 'service_address')} hint={tr('admin', 'given_by_the_service_when_this_facility_is')}>
            <Input value={baseUrl} disabled={locked} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://" />
          </Field>
          {info.settingFields.map((field) => (
            <Field key={field.key} label={field.label} hint={field.hint} required={field.required}>
              {field.options ? (
                <Select value={settings[field.key] ?? ''} disabled={locked} onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}>
                  {!field.required && <option value="">{tr('admin', 'use_the_default')}</option>}
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={settings[field.key] ?? ''}
                  disabled={locked}
                  onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
                />
              )}
            </Field>
          ))}
        </div>

        <div className="space-y-3 rounded-md border p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tr('admin', 'credentials_stored_encrypted_on_this_computer')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {info.credentialFields.map((field) => {
              const saved = connection.credentialFieldsSet.includes(field.key);
              const placeholder = saved ? 'Saved — type to replace' : '';
              return (
                <Field key={field.key} label={field.label} hint={field.hint}>
                  {field.multiline ? (
                    <Textarea rows={3} value={credentials[field.key] ?? ''} disabled={locked} placeholder={placeholder} onChange={(e) => setCredentials({ ...credentials, [field.key]: e.target.value })} />
                  ) : (
                    <Input type="password" autoComplete="off" value={credentials[field.key] ?? ''} disabled={locked} placeholder={placeholder} onChange={(e) => setCredentials({ ...credentials, [field.key]: e.target.value })} />
                  )}
                </Field>
              );
            })}
          </div>
        </div>

        {testResult && <Alert variant={testResult.ok ? 'success' : 'destructive'}>{testResult.detail}</Alert>}
        {connection.lastTestedAt && !testResult && (
          <p className="text-xs text-muted-foreground">
            {tr('admin', 'last_test')} {relative(connection.lastTestedAt)}: {connection.lastTestOk ? tr('admin', 'reachable') : tr('admin', 'failed')} — {connection.lastTestDetail}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {!locked && (
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              {tr('admin', 'save')}
            </Button>
          )}
          {connection.licensed && connection.runsHere && !readOnly && (
            <Button variant="outline" loading={test.isPending} onClick={() => test.mutate()}>
              {tr('admin', 'test_the_connection')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MessageLog() {
  const toast = useToast();
  const { can } = useAuth();
  const mayView = (row: IntegrationMessageDto) => (row.provider === 'nphies' ? can('nphies.messages.read') : can('integration.read', 'integration.manage'));
  const mayAct = (row: IntegrationMessageDto) => can(row.provider === 'nphies' ? nphiesOperationPermission(row.operation) : 'integration.manage');
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState<IntegrationMessageDetailDto | null>(null);

  const messages = useQuery({
    queryKey: ['integration-messages', status],
    queryFn: () => get<IntegrationMessagePageDto>(`/integrations/messages${qs({ status: status || undefined, limit: 50 })}`),
    refetchInterval: visibleInterval(REFRESH_MS),
    placeholderData: (previous) => previous,
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'retry' | 'cancel' }) => post<IntegrationMessageDto>(`/integrations/messages/${id}/${action}`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['integration-messages'] }),
    onError: (error) => toast.error(tr('admin', 'could_not_do_that'), describeError(error)),
  });

  const open = useMutation({
    mutationFn: (id: string) => get<IntegrationMessageDetailDto>(`/integrations/messages/${id}`),
    onSuccess: setDetail,
    onError: (error) => toast.error(tr('admin', 'could_not_open_it'), describeError(error)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('admin', 'what_was_sent')}</CardTitle>
      </CardHeader>
      <CardContent>
        <SearchFilterBar
          filters={
            <FilterField label={tr('admin', 'status')}>
              <Select className="w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{tr('admin', 'every_status')}</option>
                {INTEGRATION_MESSAGE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {statusLabel(value)}
                  </option>
                ))}
              </Select>
            </FilterField>
          }
          trailing={
            <Button variant="outline" size="sm" className="gap-1.5" disabled={messages.isFetching} onClick={() => void messages.refetch()}>
              <RefreshCw className={messages.isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} /> {tr('admin', 'refresh')}
            </Button>
          }
          onReset={status ? () => setStatus('') : undefined}
        />
        <DataTable
          dense
          rows={messages.data?.items}
          loading={messages.isLoading} refreshing={messages.isFetching}
          error={messages.error}
          rowKey={(row: IntegrationMessageDto) => row.id}
          empty={tr('admin', 'nothing_has_been_sent_to_an_external_service_yet')}
          columns={[
            { header: tr('admin', 'when'), sort: (row: IntegrationMessageDto) => row.createdAt, cell: (row: IntegrationMessageDto) => <span className="whitespace-nowrap text-xs">{dateTime(row.createdAt)}</span> },
            { header: tr('admin', 'service'), sort: (row: IntegrationMessageDto) => row.provider, cell: (row: IntegrationMessageDto) => humanize(row.provider) },
            { header: tr('admin', 'request'), sort: (row: IntegrationMessageDto) => row.operation, cell: (row: IntegrationMessageDto) => statusLabel(row.operation) },
            { header: tr('admin', 'status'), sort: (row: IntegrationMessageDto) => row.status, cell: (row: IntegrationMessageDto) => <StatusBadge status={row.status} tone={STATUS_BADGE[row.status]} /> },
            {
              header: tr('admin', 'detail'),
              cell: (row: IntegrationMessageDto) => (
                <span className="text-xs text-muted-foreground">
                  {row.errorMessage ?? row.summary ?? (row.latencyMs !== null ? tr('admin', 'x_ms', { latencyMs: row.latencyMs }) : '')}
                  {row.status === 'queued' && row.nextAttemptAt ? ` · next try ${relative(row.nextAttemptAt)}` : ''}
                </span>
              ),
            },
            {
              header: '',
              align: 'right',
              cell: (row: IntegrationMessageDto) => (
                <span className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => open.mutate(row.id)} disabled={!mayView(row)}>
                    {tr('admin', 'view')}
                  </Button>
                  {mayAct(row) && row.status === 'queued' && (
                    <Button size="sm" variant="outline" onClick={() => act.mutate({ id: row.id, action: 'cancel' })}>
                      {tr('admin', 'cancel')}
                    </Button>
                  )}
                  {mayAct(row) && ['failed', 'offline', 'cancelled'].includes(row.status) && (
                    <Button size="sm" variant="outline" onClick={() => act.mutate({ id: row.id, action: 'retry' })}>
                      {tr('admin', 'try_again')}
                    </Button>
                  )}
                </span>
              ),
            },
          ]}
        />
      </CardContent>
      <Dialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={detail ? `${humanize(detail.provider)} · ${statusLabel(detail.operation)}` : ''}
        description={tr('admin', 'the_message_text_itself_is_not_kept_this_is_the')}
        footer={<Button variant="ghost" onClick={() => setDetail(null)}>{tr('admin', 'close')}</Button>}
      >
        {detail && (
          <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-2 text-sm">
            <dt className="text-muted-foreground">{tr('admin', 'message_id')}</dt>
            <dd className="break-all font-mono text-xs">{detail.correlationId}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'kind')}</dt>
            <dd>{statusLabel(detail.event ?? detail.operation)}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'status')}</dt>
            <dd><StatusBadge status={detail.status} tone={STATUS_BADGE[detail.status]} /></dd>
            <dt className="text-muted-foreground">{tr('admin', 'summary')}</dt>
            <dd>{detail.summary ?? '—'}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'fingerprint')}</dt>
            <dd className="break-all font-mono text-xs">{detail.hash ?? '—'}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'size')}</dt>
            <dd>{detail.bytes !== null ? tr('admin', 'x_bytes', { bytes: detail.bytes }) : '—'}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'tries')}</dt>
            <dd>{detail.attempts}{detail.httpStatus ? ` · last answer ${detail.httpStatus}` : ''}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'created')}</dt>
            <dd>{dateTime(detail.createdAt)}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'sent')}</dt>
            <dd>{detail.sentAt ? dateTime(detail.sentAt) : '—'}</dd>
            <dt className="text-muted-foreground">{tr('admin', 'finished')}</dt>
            <dd>{detail.completedAt ? dateTime(detail.completedAt) : '—'}</dd>
            {detail.errorMessage && (
              <>
                <dt className="text-muted-foreground">{tr('admin', 'problem')}</dt>
                <dd className="text-destructive">{detail.errorCode ? `${detail.errorCode}: ` : ''}{detail.errorMessage}</dd>
              </>
            )}
          </dl>
        )}
      </Dialog>
    </Card>
  );
}

const LOCAL_TYPES: { value: IntegrationLocalType; label: string }[] = [
  { value: 'payer', label: 'Insurer (licence number)' },
  { value: 'charge_item', label: 'Billed service' },
  { value: 'product', label: 'Drug or product' },
];

function CodeMapping({ readOnly, provider }: { readOnly: boolean; provider: IntegrationProviderCode }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [localType, setLocalType] = useState<IntegrationLocalType>(provider === 'wasfaty' ? 'product' : 'payer');
  const [localId, setLocalId] = useState('');
  const [externalSystem, setExternalSystem] = useState('');
  const [externalCode, setExternalCode] = useState('');
  const [externalDisplay, setExternalDisplay] = useState('');

  const choices = useQuery({
    queryKey: ['integration-local-choices', localType],
    queryFn: async (): Promise<{ id: string; label: string }[]> => {
      if (localType === 'payer') return (await get<{ id: string; code: string; name: string }[]>('/insurance/payers')).map((row) => ({ id: row.id, label: `${row.code} — ${row.name}` }));
      if (localType === 'charge_item') return (await get<{ data: { id: string; code: string; name: string }[] }>('/charge-items?limit=300')).data.map((row) => ({ id: row.id, label: `${row.code} — ${row.name}` }));
      return (await get<{ id: string; code: string; genericName: string }[]>('/pharmacy/products?limit=200')).map((row) => ({ id: row.id, label: `${row.code} — ${row.genericName}` }));
    },
    enabled: !readOnly,
  });

  const maps = useQuery({
    queryKey: ['integration-code-maps', provider],
    queryFn: () => get<{ items: IntegrationCodeMapDto[] }>(`/integrations/code-maps${qs({ provider, limit: 100 })}`),
  });

  const gapType = localType === 'product' ? 'product' : 'charge_item';
  const gapsWanted = provider === 'nphies' && localType !== 'payer' && (localType === 'product' || localType === 'charge_item') && !readOnly;
  const gaps = useInfiniteQuery({
    queryKey: ['integration-code-gaps', gapType],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<IntegrationCodeGapPageDto>(`/integrations/code-gaps${qs({ localType: gapType, limit: 15, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: gapsWanted,
  });
  const gapRows = gaps.data?.pages.flatMap((page) => page.items);

  const save = useMutation({
    mutationFn: () =>
      put('/integrations/code-maps', { provider, localType, localId, externalSystem: externalSystem.trim(), externalCode: externalCode.trim(), ...(externalDisplay.trim() ? { externalDisplay: externalDisplay.trim() } : {}) }),
    onSuccess: () => {
      setLocalId('');
      setExternalCode('');
      setExternalDisplay('');
      void queryClient.invalidateQueries({ queryKey: ['integration-code-gaps'] });
      void queryClient.invalidateQueries({ queryKey: ['integration-code-maps', provider] });
      toast.success(tr('admin', 'mapping_saved'));
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_mapping'), describeError(error)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/integrations/code-maps/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['integration-code-maps', provider] }),
    onError: (error) => toast.error(tr('admin', 'could_not_remove_it'), describeError(error)),
  });

  const nameOf = (row: IntegrationCodeMapDto) => (choices.data ?? []).find((choice) => choice.id === row.localId)?.label ?? row.localId;

  return (
    <div className="space-y-3">
      {!readOnly && (
        <div className="grid gap-3 sm:grid-cols-5">
          <ChoiceButtons
            className="sm:col-span-5"
            aria-label={tr('admin', 'type')}
            value={localType}
            onChange={(value) => { setLocalType(value); setLocalId(''); }}
            options={LOCAL_TYPES}
          />
          <Select className="sm:col-span-2" value={localId} onChange={(e) => setLocalId(e.target.value)}>
            <option value="">{tr('admin', 'choose')}</option>
            {localId && !(choices.data ?? []).some((choice) => choice.id === localId) && (
              <option value={localId}>{gapRows?.find((row) => row.localId === localId)?.name ?? localId}</option>
            )}
            {(choices.data ?? []).map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.label}
              </option>
            ))}
          </Select>
          <Input placeholder={tr('admin', 'code_system')} value={externalSystem} onChange={(e) => setExternalSystem(e.target.value)} />
          <Input placeholder={tr('admin', 'their_code')} value={externalCode} onChange={(e) => setExternalCode(e.target.value)} />
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!localId || !externalSystem.trim() || !externalCode.trim()}>
            {tr('admin', 'add')}
          </Button>
          <Input className="sm:col-span-5" placeholder={tr('admin', 'their_name_for_it_optional')} value={externalDisplay} onChange={(e) => setExternalDisplay(e.target.value)} />
        </div>
      )}
      {gapsWanted && (gapRows?.length ?? 0) > 0 && (
        <div className="space-y-2 rounded-md border border-warning/40 p-3">
          <p className="text-sm font-medium">{gapType === 'product' ? tr('admin', 'drugs_and_products') : tr('admin', 'billed_services')} {tr('admin', 'that_have_no_insurer_code_yet')}</p>
          <p className="text-xs text-muted-foreground">{tr('admin', 'a_claim_carrying_one_of_these_cannot_be_sent')}</p>
          <DataTable
            dense
            rows={gapRows}
            rowKey={(row) => row.localId}
            columns={[
              { header: tr('admin', 'code'), sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              { header: tr('admin', 'name'), sort: (row) => row.name, cell: (row) => row.name },
              {
                header: '',
                align: 'right',
                cell: (row) => (
                  <Button size="sm" variant="outline" onClick={() => setLocalId(row.localId)}>
                    {tr('admin', 'map_this')}
                  </Button>
                ),
              },
            ]}
            footer={
              gaps.hasNextPage && (
                <Button size="sm" variant="ghost" loading={gaps.isFetchingNextPage} onClick={() => void gaps.fetchNextPage()}>
                  {tr('admin', 'show_more')}
                </Button>
              )
            }
          />
        </div>
      )}
      <DataTable
        dense
        rows={maps.data?.items}
        loading={maps.isLoading} refreshing={maps.isFetching}
        error={maps.error}
        rowKey={(row: IntegrationCodeMapDto) => row.id}
        empty={tr('admin', 'nothing_mapped_yet')}
        columns={[
          { header: tr('admin', 'type'), sort: (row: IntegrationCodeMapDto) => row.localType, cell: (row: IntegrationCodeMapDto) => LOCAL_TYPES.find((type) => type.value === row.localType)?.label ?? humanize(row.localType) },
          { header: tr('admin', 'ours'), sort: (row: IntegrationCodeMapDto) => nameOf(row), cell: (row: IntegrationCodeMapDto) => nameOf(row) },
          { header: tr('admin', 'theirs'), sort: (row: IntegrationCodeMapDto) => row.externalCode, cell: (row: IntegrationCodeMapDto) => <span className="font-mono text-xs">{row.externalCode}</span> },
          { header: tr('admin', 'system_2'), sort: (row: IntegrationCodeMapDto) => row.externalSystem, cell: (row: IntegrationCodeMapDto) => <span className="text-xs text-muted-foreground">{row.externalSystem}</span> },
          {
            header: '',
            align: 'right',
            cell: (row: IntegrationCodeMapDto) =>
              !readOnly && (
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(row.id)}>
                  {tr('admin', 'remove')}
                </Button>
              ),
          },
        ]}
      />
    </div>
  );
}

export function IntegrationsTab() {
  const { can } = useAuth();
  const mayConfigure = (provider: IntegrationProviderCode) => can(providerSettingsPermission(provider));
  const connections = useQuery({
    queryKey: ['integration-connections'],
    queryFn: () => get<IntegrationConnectionDto[]>('/integrations/connections'),
    refetchInterval: visibleInterval(REFRESH_MS),
  });
  const licensed = (connections.data ?? []).filter((connection) => connection.licensed);

  return (
    <div className="space-y-4">
      <Alert>
        {tr('admin', 'requests_to_nphies_and_wasfaty_are_sent_by_this')}
      </Alert>
      {(connections.data ?? []).map((connection) => (
        <ConnectionCard key={`${connection.provider}-${connection.info.code}`} connection={connection} readOnly={!mayConfigure(connection.provider)} />
      ))}
      {licensed
        .filter((connection) => connection.provider === 'nphies')
        .map((connection) => (
          <Card key={`maps-${connection.provider}`}>
            <CardHeader>
              <CardTitle className="text-sm">{tr('admin', 'code_mapping')} {connection.info.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <CodeMapping readOnly={!mayConfigure(connection.provider)} provider={connection.provider} />
            </CardContent>
          </Card>
        ))}
      {licensed.some((connection) => connection.provider === 'nphies') && can('nphies.settings') && <NphiesSetupPanels />}
      {licensed.some((connection) => connection.provider === 'nphies') && can('nphies.settings') && <NphiesReplyTemplatesPanel />}
      {licensed.some((connection) => connection.provider === 'nphies') && can('nphies.archive.read') && <NphiesArchivePanel />}
      {licensed.length > 0 && can('integration.read', 'integration.manage', 'nphies.messages.read') && <MessageLog />}
    </div>
  );
}
